import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { confirmEmail, resendConfirmation } from "@/lib/auth";
import { keys } from "@/lib/queries";
import { humanizeError } from "@/lib/errorMessages";
import { Button, Card, Input, Field, Spinner } from "@/components/ui";
import { BrandLogoMark, useBrandName } from "@/brand";
import { buildHead } from "@/lib/seo";

// Public landing for the email-confirmation magic link. Reads ?token,
// confirms on mount (which the gateway turns into verify + auto-login),
// then offers a way into the app. On an invalid/expired link it falls
// back to a resend form.
export const Route = createFileRoute("/confirm-email")({
  head: () => buildHead({ path: "/confirm-email", title: "Confirm your email", noindex: true }),
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
  component: ConfirmEmailPage,
});

type Phase = "verifying" | "success" | "error" | "missing";

function ConfirmEmailPage() {
  const { token } = Route.useSearch();
  const { t } = useTranslation("auth");
  const brand = useBrandName();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>(token ? "verifying" : "missing");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  // Guard against React 18 StrictMode's double-mount firing confirm twice
  // (the second call would hit an already-consumed token → false error).
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    void (async () => {
      try {
        await confirmEmail(token);
        await qc.invalidateQueries({ queryKey: keys.me() });
        setPhase("success");
      } catch (e) {
        setErrMsg(humanizeError(e));
        setPhase("error");
      }
    })();
  }, [token, qc]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <Card className="w-full max-w-sm p-6">
        <div className="space-y-4 text-center">
          <div className="flex items-center justify-center gap-1.5 text-fg">
            <BrandLogoMark className="h-5 w-5 text-primary" />
            <span className="font-semibold">{brand}</span>
          </div>

          {phase === "verifying" && (
            <div className="space-y-3 py-2">
              <Spinner className="mx-auto text-primary" size={24} />
              <p className="text-sm text-fg-muted">{t("confirm.verifying")}</p>
            </div>
          )}

          {phase === "success" && (
            <div className="space-y-3">
              <h1 className="text-lg font-semibold text-fg">{t("confirm.successTitle")}</h1>
              <p className="text-sm text-fg-muted">{t("confirm.successBody")}</p>
              <Button block onClick={() => navigate({ to: "/" })}>
                {t("confirm.continue", { brand })}
              </Button>
            </div>
          )}

          {phase === "missing" && (
            <div className="space-y-3">
              <h1 className="text-lg font-semibold text-fg">{t("confirm.missingTitle")}</h1>
              <p className="text-sm text-fg-muted">{t("confirm.missingBody")}</p>
              <Button block variant="outline" onClick={() => navigate({ to: "/" })}>
                {t("confirm.goHome")}
              </Button>
            </div>
          )}

          {phase === "error" && <ResendPanel message={errMsg} />}
        </div>
      </Card>
    </div>
  );
}

// ResendPanel is shown when the link is invalid/expired: a short message
// plus a form to request a fresh confirmation email.
function ResendPanel({ message }: { message: string | null }) {
  const { t } = useTranslation("auth");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const resend = async () => {
    if (!email || busy) return;
    setBusy(true);
    setErr(null);
    setSent(false);
    try {
      await resendConfirmation(email);
      setSent(true);
    } catch (e) {
      setErr(humanizeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <h1 className="text-lg font-semibold text-fg">{t("confirm.errorTitle")}</h1>
      <p className="text-sm text-danger">{message ?? t("confirm.errorFallback")}</p>
      {sent ? (
        <p className="text-sm text-success">{t("confirm.resent")}</p>
      ) : (
        <div className="space-y-2 text-left">
          <Field label={t("confirm.resendLabel")} htmlFor="resend-email">
            <Input
              id="resend-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("fields.emailPlaceholder")}
            />
          </Field>
          {err && <p className="text-sm text-danger">{err}</p>}
          <Button block loading={busy} disabled={!email} onClick={resend}>
            {t("pending.resendConfirmation")}
          </Button>
        </div>
      )}
    </div>
  );
}
